import Selection from "../js/utils/Selection.js";
import CartController from "./CartController.js";

// Refactore: EngineController -> PanelFinderController
export default class EngineController {
	constructor(model, config, features) {
		this.model = model;
		this.config = config;
		this.features = features;

		this.selectedPlus = { value: {} };
		this.selectedDetail = { value: {} };
		this.selectedFoldable = { value: {} };
		this.selectedImage = { value: "" };
		this.isFading = { value: false };
		this.hoveredDetail = { value: {} };
		this.hoveredPlus = { value: {} };
		this.panelItems = { value: model.panelItems };
		this.selectedItems = { value: "" };
		this.pluses = new Selection(model.pluses, { multiple: true });
		this.cart = model.cart;
		this.navigationItems = model.navigationItems;
		this.filteredNavigationItems = { value: [] };

		this.cartController = new CartController(
			new Selection(model.cartItems, { multiple: true }),
		);
		this.categories = new Selection(model.categories, { multiple: true });
		this.engines = new Selection(model.engines);
		this.markedDetails = new Selection([], { multiple: true });
		this.markedPluses = new Selection(model.pluses, { multiple: true });

		if (this.config.engines) {
			this.filterPanelItems(this.model.panelItems, 1);
		}
	}

	// Refactore: engineSelectAction -> panelFinderSelectAction
	engineSelectAction(engineId) {
		this.engines.selectByProperty("id", engineId);
		this.filterPanelItems(this.model.panelItems, engineId);
		this.selectedImage.value = this.engines.selection.value.image;
		this.resetSelection();
	}

	filterPanelItems(items, engineId) {
		const itemsCopy = JSON.parse(JSON.stringify(items));
		function recursiveFilter(items) {
			return items
				.map((item) => {
					if (item.children) {
						item.children = recursiveFilter(item.children);
					}
					return item;
				})
				.filter(
					(item) =>
						item.engines.length === 0 || item.engines.includes(engineId),
				);
		}

		this.filteredNavigationItems.value = this.getNavigationItems(
			recursiveFilter(itemsCopy),
		);
	}

	getNavigationItems(items) {
		const navigationItems = this.model.navigation.map((nav) => ({
			...nav,
			panelItems: nav.panelItems
				.map((id) => {
					return items.find((panel) => panel.id === id);
				})
				.filter(Boolean),
		}));
		return navigationItems;
	}

	generateEngineNavigation() {
		return {
			value: this.model.engines.map((engine) => ({
				id: engine.id,
				title: engine.text,
				hash: `#${engine.id}`,
			})),
		};
	}

	markerClickAction(marker, action) {
		if (action === "add") {
			this.categories.select(marker);
		} else if (action === "remove") {
			this.categories.deselect(marker);
		}

		const details = this.getDetailsByCategories(
			this.categories.selection.value,
		);

		this.markedDetails.reset();
		this.markedPluses.reset();

		details.forEach((detail) => {
			this.markedDetails.select(detail);

			const plus = this.markedPluses.items.find((p) => {
				return p.typeItemId === detail.parentId;
			});
			if (plus) {
				this.markedPluses.select(plus);
			}
		});
	}

	getDetailsByCategories(categories) {
		const details = [];
		categories.forEach((category) => {
			if (category.id in this.model.searchDetailByCategoriesStructure) {
				details.push(
					...this.model.searchDetailByCategoriesStructure[category.id],
				);
			}
		});
		return [...new Set(details)];
	}

	setSelectedPlus(plus) {
		this.selectedPlus.value = plus || {};

		if (!plus) {
			this.selectedDetail.value = {};
			return;
		}

		const solution = this.getPanelItem(plus.typeItemId, plus.type);

		if (!solution) return;

		const hasChildren = solution.children?.length > 0;
		const selectedDetailIndex = hasChildren
			? solution.children.findIndex((item) => item.id === plus.id)
			: -1;

		this.setSelectedDetail(
			hasChildren
				? solution.children[
						selectedDetailIndex === -1 ? 0 : selectedDetailIndex
					]
				: solution,
		);

		this.selectedFoldable.value = solution;
	}

	setFading(value) {
		this.isFading.value = value;
	}

	setSelectedDetail(detail) {
		if (this.selectedDetail.value.id > 0) {
			this.setFading(true);
			setTimeout(() => {
				this.selectedDetail.value = detail;
				this.setFading(false);
			}, 100);
		} else {
			this.selectedDetail.value = detail;
		}
	}

	onHoveredPlus(plus) {
		if (plus) {
			const solution = this.getPanelItem(plus.typeItemId, plus.type);

			if (solution) {
				this.hoveredDetail.value = solution;
				this.hoveredPlus.value = plus;
			}
		} else {
			this.hoveredDetail.value = {};
			this.hoveredPlus.value = {};
		}
	}

	onHoveredDetail(solution) {
		if (solution.id) {
			this.hoveredPlus.value = this.findPlus(solution);
			this.hoveredDetail.value = solution;
		} else {
			this.hoveredPlus.value = {};
			this.hoveredDetail.value = {};
		}
	}

	setFoldableSelected(detail) {
		const plus = this.findPlus(detail);
		this.setVisiblePlusesByFoldable(detail);
		this.selectedFoldable.value = detail;
		this.selectedPlus.value = plus;
	}

	selectPlusByPanelItem(panelItem) {
		let plus = this.findPlus(panelItem);
		if (!plus) {
			const panelItemParent = this.findPanelItemAndParent(
				panelItem.id,
				panelItem.type,
			)?.parent || { item: { id: 0 } };
			plus = this.findPlus(panelItemParent);
		}
		this.selectedPlus.value = plus;
	}

	findSolution(itemId) {
		return this.panelItems.value.find((item) => item.id === itemId);
	}

	findPlus(solution) {
		return this.pluses.items.find(
			(plus) => plus.typeItemId == solution.id && plus.type == solution.type,
		);
	}

	setVisiblePlusesByFoldable(foldable) {
		this.pluses.reset();

		if (!foldable || Object.keys(foldable).length === 0) {
			return;
		}

		const childIds = foldable.children.map((child) => child.id);
		this.pluses.items.forEach((plus) => {
			if (plus.allTimeVisible) {
				this.pluses.select(plus);
				return;
			}

			// if plus is pointing on panel parent
			if (foldable.id === plus.typeItemId && foldable.children.length) {
				this.pluses.select(plus);
			} else {
				// if plus is pointing on panel child
				const isPlusOfChild = childIds.find((item) => item === plus.typeItemId);

				if (isPlusOfChild) {
					this.pluses.select(plus);
				}
			}
		});
	}

	isFoldableActive(foldable) {
		return foldable.children.find((detail) => {
			return detail.id === this.selectedDetail.value.id;
		});
	}

	navigationDetailClickedAction(detail, solution) {
		this.setSelectedDetail(detail);
		this.setFoldableSelected(solution);
		this.selectPlusByPanelItem(detail);
	}

	resetDetailSelection() {
		this.setSelectedDetail({ id: 0 });
		this.selectPlusByPanelItem({ id: 0 });
	}

	resetSelection() {
		this.resetDetailSelection();
		this.setFoldableSelected({});
	}

	navigationFoldableClickedAction(solution) {
		this.setFoldableSelected(solution);
	}

	navigationDetailHoveredAction(detail) {
		// intentionally left empty
	}

	closeNavigation() {
		this.resetDetailSelection();
	}

	navigationFoldableHoveredAction(solution = {}) {
		this.onHoveredDetail(solution);
	}

	checkoutCart(items) {
		console.log("Not implemented", items);
	}

	findPanelItemAndParent(
		id,
		type,
		items = this.panelItems.value,
		parent = null,
	) {
		for (const item of items) {
			if (item.id === id && item.type === type) {
				return { item, parent };
			}
			if (item.children) {
				const result = this.findPanelItemAndParent(
					id,
					type,
					item.children,
					item,
				);
				if (result) {
					return result;
				}
			}
		}
		return null;
	}

	getPanelItem(id, type) {
		const result = this.findPanelItemAndParent(id, type);
		return result ? result.item : null;
	}

	getParentPanelItem(id, type) {
		const result = this.findPanelItemAndParent(id, type);
		return result ? result.parent : null;
	}
}
